import React from 'react'
import { useBrickStore, type CameraPreset } from '@store/brickStore'
import { CAMERA_PRESETS } from '@components/canvas/CameraController'
import { Maximize2, ArrowDown, ArrowRight, MoveDownRight } from 'lucide-react'
import './CameraControls.css'

/**
 * CameraControls Component
 *
 * UI controls for switching between camera preset views.
 *
 * Features:
 * - 4 preset buttons: Isometric (1), Top (2), Front (3), Side (4)
 * - Visual indicator for active preset
 * - Keyboard shortcuts displayed (1-4)
 * - Camera icon for each preset
 * - Accessible with ARIA labels
 * - Responsive design
 *
 * Keyboard Shortcuts:
 * - 1: Isometric view (default)
 * - 2: Top view (looking down)
 * - 3: Front view (looking at origin)
 * - 4: Side view (looking at origin)
 *
 * @component
 */
export const CameraControls: React.FC = () => {
  const activeCameraPreset = useBrickStore((state) => state.activeCameraPreset)
  const setActiveCameraPreset = useBrickStore(
    (state) => state.setActiveCameraPreset
  )

  /**
   * Handle camera preset button click
   */
  const handlePresetClick = (preset: CameraPreset) => {
    setActiveCameraPreset(preset)
  }

  /**
   * Get icon for camera preset
   */
  const getPresetIcon = (preset: CameraPreset): React.ReactNode => {
    const iconSize = 18
    switch (preset) {
      case 'isometric':
        return <Maximize2 size={iconSize} /> // Isometric perspective
      case 'top':
        return <ArrowDown size={iconSize} /> // Looking down
      case 'front':
        return <ArrowRight size={iconSize} /> // Looking forward
      case 'side':
        return <MoveDownRight size={iconSize} /> // Looking from side
      default:
        return <Maximize2 size={iconSize} />
    }
  }

  return (
    <div className="camera-controls">
      <div className="camera-controls__label">Camera</div>

      <div className="camera-controls__buttons">
        {(Object.entries(CAMERA_PRESETS) as [CameraPreset, typeof CAMERA_PRESETS[CameraPreset]][]).map(
          ([preset, config]) => {
            const isActive = activeCameraPreset === preset

            return (
              <button
                key={preset}
                className={`camera-controls__button ${
                  isActive ? 'camera-controls__button--active' : ''
                }`}
                onClick={() => handlePresetClick(preset)}
                title={`${config.label} (Press ${config.key})`}
                aria-label={`Switch to ${config.label}`}
                aria-pressed={isActive}
              >
                <span className="camera-controls__icon">
                  {getPresetIcon(preset)}
                </span>
                <span className="camera-controls__button-label">
                  {config.label}
                </span>
                <span className="camera-controls__key-hint">{config.key}</span>
              </button>
            )
          }
        )}
      </div>
    </div>
  )
}
